'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { useTranslation } from '../../lib/i18n';
import { holdsAnswer, SaveConflictError } from '../../lib/saveFeedback';
import { ConversationNotificationsRefreshError, setConversationNotifications } from '../../lib/mutations/conversationNotifications';
import { openBrainSession } from '../../lib/brainDock';
import { authGeneration } from '../../lib/token';
import { useConversationJobLinks, useMe, useMyAlertSettings, useProjects } from '../../lib/queries';
import { invalidateConversationLists, renameConversation, setConversationPinned, setConversationArchive } from '../../lib/mutations/brain';
import { useBrainChat } from './BrainChatProvider';
import type { ConversationLinksResponse, BrainSearchHit, BrainSearchScope, Project } from '../../lib/types';
import type {
  ConversationListExportFormat,
  ConversationListItem,
  ConversationListReadState,
  ConversationListView,
} from './conversationListModel';

export interface ConversationListControllerOptions {
  search: string;
  pageIds: readonly string[];
  onNavigate?: () => void;
}

export type ConversationListBranchReadState = ConversationListReadState | { status: 'unavailable' };

/** Mutations reject on failure, return false when scope/lifetime changed, and true on success.
 * Export success means a browser download was initiated, not that a file was saved.
 * Confirmation, batching, selection and sub-agent drill-in belong to the panel. */
export interface ConversationListController {
  items: ConversationListItem[];
  itemById: ReadonlyMap<string, ConversationListItem>;
  activeId: string | null;
  authorized: boolean;
  isAdmin: boolean;
  projects: readonly Project[] | undefined;
  read: {
    sessions: ConversationListReadState;
    personal: ConversationListReadState;
    projects: ConversationListReadState;
    search: ConversationListReadState;
    jobs: ConversationListBranchReadState;
    subagents: ConversationListBranchReadState;
  };
  searchHits: readonly BrainSearchHit[] | null;
  snippets: ReadonlyMap<string, BrainSearchHit>;
  links: ConversationLinksResponse | undefined;
  open: (id: string) => boolean;
  rename: (id: string, title: string) => Promise<boolean>;
  fork: (id: string) => Promise<boolean>;
  setPinned: (id: string, pinned: boolean) => Promise<boolean>;
  setArchived: (id: string, archived: boolean) => Promise<boolean>;
  exportSession: (id: string, format: ConversationListExportFormat) => Promise<boolean>;
  deleteSession: (id: string) => Promise<boolean>;
  leave: (id: string) => Promise<boolean>;
  mute: (item: ConversationListItem) => void;
  mutePending: (id: string) => boolean;
  deleteAll: () => Promise<boolean>;
  /** Reads the conversation listings again and rejects when a read fails: the recovery of a write whose outcome is unknown. */
  reload: () => Promise<void>;
}

function readState(query: { error: unknown; data: unknown; refetch: () => unknown }): ConversationListReadState {
  if (query.error) return { status: 'error', error: query.error, retry: () => { void query.refetch(); } };
  return query.data === undefined ? { status: 'loading' } : { status: 'ready' };
}

export function useConversationListController(
  view: ConversationListView,
  options: ConversationListControllerOptions,
): ConversationListController {
  const chat = useBrainChat();
  const { t } = useTranslation();
  const { toast } = useToast();
  const reportFailure = useOperationFailure();
  const alerts = useMyAlertSettings();
  const muteLocks = useRef(new Set<string>());
  const [muting, setMuting] = useState<ReadonlySet<string>>(new Set());
  const qc = useQueryClient();
  const me = useMe();
  const isAdmin = me.data?.user?.is_admin === true && !me.error;
  const myId = me.data?.user?.id;
  const authorized = view === 'mine' || isAdmin;
  const allEnabled = view === 'all' && authorized;
  const managed = useQuery({
    queryKey: ['brain-managed-sessions'],
    queryFn: () => elowenClient.brainManagedSessions('all'),
    enabled: allEnabled,
  });
  const projects = useProjects();
  const branches = useConversationJobLinks(view, options.pageIds, authorized);

  const personalById = useMemo(() => new Map(chat.sessions.data?.map(item => [item.id, item])), [chat.sessions.data]);
  const projectById = useMemo(() => new Map(projects.data?.map(project => [project.id, project])), [projects.data]);
  const personalReady = chat.sessions.data !== undefined && !chat.sessions.error;
  const items = useMemo<ConversationListItem[]>(() => {
    if (!authorized) return [];
    const projectFields = (ref: ConversationListItem['projectRef'], identity: ConversationListItem['projectIdentity']) => ({
      project: ref?.projectId === undefined ? null : projectById.get(ref.projectId) ?? null,
      projectIdentity: identity,
      // A Project the daemon still names for this conversation is not unavailable, only not the reader's own.
      projectUnavailable: projects.isSuccess && ref?.projectId !== undefined && !projectById.has(ref.projectId) && !identity,
    });
    if (view === 'mine') return (chat.sessions.data ?? []).map(session => ({
      ...session, kind: 'conversation', ...projectFields(session.projectRef, session.projectIdentity),
      unread: personalReady ? session.activity?.unread ?? false : null,
      pinnedAt: personalReady ? session.pinnedAt : null,
      capabilities: { ...session.capabilities, pin: personalReady && session.capabilities.pin },
    }));
    return (managed.data ?? []).map(session => {
      // The personal listing holds every conversation this account is a member of, shared ones included.
      const personal = personalReady ? personalById.get(session.id) : undefined;
      return {
        ...session, ...projectFields(session.projectRef, personal?.projectIdentity),
        ...(personal ? { notifications: personal.notifications } : {}),
        activity: session.activity,
        unread: personal ? personal.activity?.unread ?? false : null,
        pinnedAt: personal?.pinnedAt ?? null,
        // Pinning lives on the personal row; an administrator deletes any listed session through oversight
        // (`brainDeleteManagedSession`), which the membership projection deliberately does not grant.
        capabilities: { ...session.capabilities, pin: personal !== undefined && session.capabilities.pin, delete: true },
      };
    });
  }, [authorized, view, chat.sessions.data, managed.data, personalReady, personalById, projectById, projects.isSuccess]);
  const itemById = useMemo(() => new Map(items.map(item => [item.id, item])), [items]);

  // The initial /me read names an existing scope, not a change of account. Credential transitions and
  // subsequent confirmed account changes fence callbacks even when the personal list was already visible.
  const auth = authGeneration();
  const lifetime = useRef({ mounted: true, view, scopeOwnerId: myId, auth, allEnabled, generation: 0 });
  const currentLifetime = lifetime.current;
  const accountChanged = myId !== undefined && currentLifetime.scopeOwnerId !== undefined && currentLifetime.scopeOwnerId !== myId;
  const credentialChanged = currentLifetime.auth !== auth;
  if (currentLifetime.view !== view || accountChanged || credentialChanged || currentLifetime.allEnabled !== allEnabled) {
    currentLifetime.generation += 1;
    currentLifetime.view = view;
    currentLifetime.allEnabled = allEnabled;
  }
  if (credentialChanged) currentLifetime.scopeOwnerId = myId;
  else if (myId !== undefined) currentLifetime.scopeOwnerId = myId;
  currentLifetime.auth = auth;
  const generation = currentLifetime.generation;
  const latest = useRef({ authorized, itemById, chat, onNavigate: options.onNavigate });
  latest.current = { authorized, itemById, chat, onNavigate: options.onNavigate };
  const downloads = useRef(new Set<AbortController>());
  useEffect(() => {
    const controllers = downloads.current;
    const life = lifetime.current;
    life.mounted = true;
    return () => {
      life.mounted = false;
      for (const controller of controllers) controller.abort();
      controllers.clear();
    };
  }, [generation]);

  const q = options.search.trim();
  const scope: BrainSearchScope = view === 'all' ? 'instance' : 'mine';
  const [debounced, setDebounced] = useState<{ q: string; scope: BrainSearchScope; generation: number } | null>(null);
  useEffect(() => {
    if (!authorized || q.length < 2) return;
    const timer = setTimeout(() => setDebounced({ q, scope, generation: lifetime.current.generation }), 300);
    return () => clearTimeout(timer);
  }, [q, scope, authorized, generation]);
  const searchEnabled = authorized && q.length >= 2 && debounced?.q === q && debounced.scope === scope
    && debounced.generation === lifetime.current.generation;
  const search = useQuery({
    queryKey: ['brain-search', scope, q],
    queryFn: () => elowenClient.brainSearch(q, scope),
    enabled: searchEnabled,
  });
  const searchHits = searchEnabled ? search.data ?? null : null;
  const snippets = useMemo(() => {
    const hits = new Map<string, BrainSearchHit>();
    for (const hit of searchHits ?? []) if (!hits.has(hit.sessionId)) hits.set(hit.sessionId, hit);
    return hits;
  }, [searchHits]);

  const current = () => lifetime.current.mounted && lifetime.current.generation === generation
    && authGeneration() === auth && latest.current.authorized;
  const target = (id: string, capability: keyof ConversationListItem['capabilities']) => {
    const item = latest.current.itemById.get(id);
    if (!current() || !item || !item.capabilities[capability]) throw new Error('Conversation action is not authorized');
    return item;
  };
  // A successful write outlives this panel; cache invalidation is not a UI side effect.
  const refresh = async () => {
    await invalidateConversationLists(qc);
    return current();
  };
  // Errors remain errors for the panel; stale operations cannot close dialogs, navigate or report success.
  const mutate = async (operation: () => Promise<unknown>, after?: () => Promise<boolean>): Promise<boolean> => {
    try {
      await operation();
      return after ? await after() : current();
    } catch (error) {
      if (!current()) return false;
      throw error;
    }
  };
  const links = authorized ? branches.data : undefined;
  const branchRead = (status: ConversationLinksResponse['status'] | undefined): ConversationListBranchReadState => {
    if (!authorized) return { status: 'unavailable' };
    if (branches.error) return readState(branches);
    if (status === undefined) return { status: 'loading' };
    if (status === 'unavailable') return { status: 'unavailable' };
    if (status === 'error') return {
      status: 'error', error: new Error('Conversation branch read failed'), retry: () => { void branches.refetch(); },
    };
    return { status: 'ready' };
  };

  return {
    items, itemById, activeId: chat.activeSessionId, authorized, isAdmin,
    projects: projects.data,
    read: {
      sessions: view === 'mine' ? readState(chat.sessions) : !authorized
        ? me.data && !me.error
          ? { status: 'error', error: new Error('Instance conversations require administrator access'), retry: () => { void me.refetch(); } }
          : readState(me)
        : readState(managed),
      personal: readState(chat.sessions), projects: readState(projects),
      search: q.length < 2 ? { status: 'ready' } : searchEnabled ? readState(search) : { status: 'loading' },
      jobs: branchRead(links?.status),
      subagents: options.pageIds.length === 0 ? { status: 'unavailable' }
        : branches.isPlaceholderData ? { status: 'loading' } : branchRead(links?.subagentStatus),
    },
    searchHits, snippets, links,
    open: id => {
      const item = latest.current.itemById.get(id);
      if (!current() || !item) return false;
      // The instance register nests a delegated session under the conversation it ran in: it opens as a
      // read-only sub-agent view named by that parent, or by itself when the register does not hold it.
      const parent = item.parentSessionId ? latest.current.itemById.get(item.parentSessionId) : undefined;
      openBrainSession(id, item.capabilities.continue, !item.parentSessionId ? { title: item.title }
        : parent ? { delegated: true, title: parent.title, name: item.title } : { delegated: true, title: item.title });
      latest.current.onNavigate?.();
      return true;
    },
    rename: async (id, title) => {
      target(id, 'rename');
      const trimmed = title.trim();
      if (!trimmed) throw new Error('Conversation title must not be empty');
      return mutate(() => renameConversation(qc, id, trimmed));
    },
    fork: async id => {
      target(id, 'fork');
      return mutate(async () => {
        const fork = await elowenClient.brainForkSession(id);
        await refresh();
        if (!current()) return;
        // Navigate before dismissing: dismissal unmounts this controller.
        openBrainSession(fork.id, true);
        latest.current.onNavigate?.();
      });
    },
    setPinned: async (id, pinned) => {
      target(id, 'pin');
      return mutate(() => setConversationPinned(qc, id, pinned));
    },
    setArchived: async (id, archived) => {
      const item = target(id, 'archive');
      if (!item.isOwner) throw new Error('Conversation archive requires owner');
      return mutate(() => setConversationArchive(qc, id, { archived, revision: item.archiveRevision }));
    },
    exportSession: async (id, format) => {
      target(id, 'export');
      const controller = new AbortController();
      downloads.current.add(controller);
      try { return await mutate(() => elowenClient.brainExportSession(id, format, controller.signal)); }
      finally { downloads.current.delete(controller); }
    },
    deleteSession: async id => {
      const item = target(id, 'delete');
      const own = view === 'mine' || (item.ownerId === myId && item.kind === 'conversation');
      return mutate(() => own ? latest.current.chat.deleteSession(id) : elowenClient.brainDeleteManagedSession(id), refresh);
    },
    mutePending: id => muting.has(`${generation}:${id}`),
    mute: item => {
      const latestItem = latest.current.itemById.get(item.id);
      const lock = `${generation}:${item.id}`;
      if (!current() || !latestItem || !(latestItem.isOwner || latestItem.capabilities.leave) || !latestItem.notifications || muteLocks.current.has(lock)) return;
      const preference = latestItem.notifications;
      muteLocks.current.add(lock);
      setMuting(new Set(muteLocks.current));
      const release = () => {
        muteLocks.current.delete(lock);
        if (lifetime.current.mounted) setMuting(new Set(muteLocks.current));
      };
      let confirmedPreference = preference;
      const reload = async () => {
        if (!current()) throw new Error('Conversation scope changed');
        const confirmed = await elowenClient.brainConversationNotifications(item.id);
        if (!current()) throw new Error('Conversation scope changed');
        await Promise.all([
          qc.refetchQueries({ queryKey: ['brain-sessions'], exact: true }, { throwOnError: true }),
          qc.refetchQueries({ queryKey: ['brain-managed-sessions'], exact: true }, { throwOnError: true }),
        ]);
        if (!current()) throw new Error('Conversation scope changed');
        const sessions = qc.getQueryData<typeof chat.sessions.data>(['brain-sessions']);
        const row = sessions?.find(session => session.id === item.id);
        if (row && (!row.notifications || row.notifications.revision < confirmed.revision)) {
          throw new Error('Conversation listing has not caught up with notifications');
        }
        confirmedPreference = confirmed;
        release();
      };
      void (async () => {
        try {
          const accountSettings = preference.muted ? (await alerts.refetch({ throwOnError: true })).data : null;
          if (preference.muted && !accountSettings) throw new Error('Account notification settings are unavailable');
          if (!current()) { release(); return; }
          await setConversationNotifications(qc, item.id, {
            muted: !preference.muted, expectedRevision: preference.revision,
          });
          try { await reload(); } catch { throw new SaveConflictError({ reload }); }
          if (current()) toast(confirmedPreference.muted ? t.brainChat.ux.conversationMuted
            : accountSettings?.muted.core?.includes('conversation.reply') ? t.brainChat.ux.globalReplyMuteHint
              : t.brainChat.ux.conversationUnmuted, 'ok', { key: `conversation-mute:${item.id}` });
        } catch (cause) {
          if (!current()) { release(); return; }
          const error = cause instanceof ConversationNotificationsRefreshError || cause instanceof ElowenApiError && cause.status === 409
            ? new SaveConflictError({ reload }) : cause;
          if (!holdsAnswer(error)) release();
          reportFailure(`conversation-mute:${item.id}`, error, { reload, message: t.brainChat.ux.conversationMuteFailed });
        }
      })();
    },
    leave: async id => {
      target(id, 'leave');
      return mutate(() => elowenClient.brainLeaveConversation(id), refresh);
    },
    reload: async () => {
      if (!current()) throw new Error('Conversation scope changed');
      await Promise.all([
        qc.cancelQueries({ queryKey: ['brain-sessions'], exact: true }),
        qc.cancelQueries({ queryKey: ['brain-managed-sessions'], exact: true }),
      ]);
      if (!current()) throw new Error('Conversation scope changed');
      await Promise.all([
        qc.fetchQuery({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all'), staleTime: 0 }),
        ...(allEnabled ? [qc.fetchQuery({ queryKey: ['brain-managed-sessions'], queryFn: () => elowenClient.brainManagedSessions('all'), staleTime: 0 })] : []),
      ]);
      if (!current()) throw new Error('Conversation scope changed');
    },
    deleteAll: async () => {
      if (!current() || view !== 'all' || !isAdmin) throw new Error('Instance deletion is not authorized');
      return mutate(() => elowenClient.brainDeleteAllManagedSessions('all'), refresh);
    },
  };
}
